import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import DashboardLayout from './DashboardLayout';
import WeeklyPrintView from './WeeklyPrintView';
import { fetchWeekly, saveWeekly, fetchMe, deleteWeekly } from '../../api/api';

const getWeekDates = (weekString) => {
  if (!weekString) return { start: '', end: '', weekDays: [] };
  const [year, week] = weekString.split('-W');
  const d = new Date(year, 0, 1 + (week - 1) * 7);
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1);
  const startDate = new Date(d.setDate(diff));
  const endDate = new Date(startDate);
  endDate.setDate(startDate.getDate() + 6);

  const formatDate = (date) => date.toLocaleDateString('pt-BR');
  const formatDayMonth = (date) => date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });

  const weekDays = [];
  let currentDate = new Date(startDate);
  for (let i = 0; i < 7; i++) {
    weekDays.push(formatDayMonth(currentDate));
    currentDate.setDate(currentDate.getDate() + 1);
  }

  return {
    start: formatDate(startDate),
    end: formatDate(endDate),
    weekDays,
  };
};

const getDefaultData = () => ({
  theme: '',
  objectives: '',
  monday: '',
  tuesday: '',
  wednesday: '',
  thursday: '',
  friday: '',
  saturday: '',
  sunday: '',
});

const WeeklyForm = () => {
  const { week: weekParam } = useParams();
  const navigate = useNavigate();
  const isEditMode = !!weekParam;

  const [week, setWeek] = useState(weekParam || '');
  const [dates, setDates] = useState({ start: '', end: '', weekDays: [] });
  const [weeklyData, setWeeklyData] = useState(getDefaultData());
  const [showSuccess, setShowSuccess] = useState(false);
  const [teacherName, setTeacherName] = useState('');
  const [showWeekends, setShowWeekends] = useState(false);

  useEffect(() => {
    const loadUser = async () => {
      try {
        const user = await fetchMe();
        if (user && user.name) {
          setTeacherName(user.name);
        }
      } catch (error) {
        console.error('Error fetching user info:', error);
      }
    };
    loadUser();
  }, []);

  useEffect(() => {
    if (week) {
      setDates(getWeekDates(week));
      const loadWeekly = async () => {
        try {
          const data = await fetchWeekly(week);
          if (data) {
            setWeeklyData(data);
            setShowWeekends(!!data.showWeekends);
          } else {
            setWeeklyData(getDefaultData());
            setShowWeekends(false);
          }
        } catch (error) {
          console.error('Error loading weekly:', error);
          setWeeklyData(getDefaultData());
          setShowWeekends(false);
        }
      };
      loadWeekly();
    } else {
      setDates({ start: '', end: '', weekDays: [] });
      setWeeklyData(getDefaultData());
      setShowWeekends(false);
    }
  }, [week]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setWeeklyData((prev) => ({ ...prev, [name]: value }));
  };

  const handleDelete = async () => {
    if (window.confirm('Tem certeza que deseja apagar este semanário?')) {
      try {
        await deleteWeekly(week);
        navigate('/dashboard/weekly/list');
      } catch (error) {
        console.error('Error deleting weekly:', error);
        alert('Erro ao apagar o semanário.');
      }
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!week) {
      alert("Por favor, selecione uma semana primeiro.");
      return;
    }

    try {
      await saveWeekly({ ...weeklyData, week, showWeekends });
      setShowSuccess(true);
      setTimeout(() => {
        setShowSuccess(false);
        if (isEditMode) {
          navigate('/dashboard/weekly/list');
        }
      }, isEditMode ? 1500 : 3000);
    } catch (error) {
      console.error('Error saving weekly:', error);
      alert(`Erro ao ${isEditMode ? 'atualizar' : 'salvar'} semanário.`);
    }
  };

  const headerAction = (
    <button type="button" className="btn btn-outline-secondary" onClick={() => window.print()}>
      <i className="bi bi-printer"></i> Imprimir
    </button>
  );

  return (
    <DashboardLayout
      title={isEditMode ? "Editar Semanário" : "Adicionar / Editar Semanário"}
      headerAction={week ? headerAction : null}
    >
      {showSuccess && (
        <div className="alert alert-success" role="alert">
          Semanário {isEditMode ? 'atualizado' : 'salvo'} com sucesso!
        </div>
      )}
      <div className="d-print-none">
        <form onSubmit={handleSave}>
          <div className="row mb-4 align-items-end">
            <div className="col-md-4">
              <label className="form-label fw-bold d-print-none">Semana do Ano</label>
              <div className="d-print-none">
                {isEditMode ? (
                  <input
                    type="text"
                    className="form-control"
                    value={week ? `Semana ${week.split('-W')[1]} (${week.split('-W')[0]})` : ''}
                    disabled
                  />
                ) : (
                  <input
                    type="week"
                    className="form-control"
                    value={week}
                    onChange={(e) => setWeek(e.target.value)}
                    required
                  />
                )}
              </div>
              <div className="d-none d-print-block mt-2 fs-5">
                <strong>Semana {week ? week.split('-W')[1] : ''} ({week ? week.split('-W')[0] : ''})</strong>
              </div>
            </div>
            <div className="col-md-8">
              {week && (
                <span className="text-muted ms-2">
                  Período: {dates.start} até {dates.end}
                </span>
              )}
            </div>
          </div>

          {week ? (
            <>
              <div className="row mb-4">
                <div className="col-md-6">
                  <label className="form-label fw-bold">Tema da Semana</label>
                  <input
                    type="text"
                    className="form-control"
                    name="theme"
                    value={weeklyData.theme}
                    onChange={handleChange}
                    placeholder="Ex: Frações no cotidiano"
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label fw-bold">Objetivos (Habilidades BNCC)</label>
                  <input
                    type="text"
                    className="form-control"
                    name="objectives"
                    value={weeklyData.objectives}
                    onChange={handleChange}
                    placeholder="Ex: EF04MA09"
                  />
                </div>
              </div>

              <div className="d-flex justify-content-between align-items-center mb-3 d-print-none">
                <h4 className="m-0">Planejamento Diário</h4>
                <div className="form-check form-switch">
                  <input className="form-check-input border border-secondary" type="checkbox" role="switch" id="showWeekends" checked={showWeekends} onChange={(e) => setShowWeekends(e.target.checked)} style={{ cursor: 'pointer' }} />
                  <label className="form-check-label fw-bold text-secondary" htmlFor="showWeekends" style={{ cursor: 'pointer' }}>Mostrar Finais de Semana</label>
                </div>
              </div>
              <div className="row g-3">
                {['monday', 'tuesday', 'wednesday', 'thursday', 'friday', ...(showWeekends ? ['saturday', 'sunday'] : [])].map((day, index) => {
                  const baseName = day === 'tuesday' ? 'Terça-feira' : day === 'wednesday' ? 'Quarta-feira' : day === 'thursday' ? 'Quinta-feira' : day === 'friday' ? 'Sexta-feira' : day === 'saturday' ? 'Sábado' : day === 'sunday' ? 'Domingo' : 'Segunda-feira';
                  const dateStr = dates.weekDays && dates.weekDays[index] ? ` (${dates.weekDays[index]})` : '';
                  const dayName = `${baseName}${dateStr}`;
                  return (
                    <div className="col-md" key={day}>
                      <div className="card h-100 shadow-sm border-0 bg-light">
                        <div className="card-header text-capitalize text-center fw-bold bg-white border-bottom-0 pt-3">
                          {dayName}
                        </div>
                        <div className="card-body p-2">
                          <textarea
                            className="form-control h-100 border-0"
                            style={{ minHeight: '180px', resize: 'none', backgroundColor: '#fdfdfd' }}
                            name={day}
                            value={weeklyData[day]}
                            onChange={handleChange}
                            placeholder="Atividades, rotina e recursos..."
                          ></textarea>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="mt-4 text-end">
                {isEditMode && (
                  <button type="button" className="btn btn-danger me-2 d-print-none" onClick={handleDelete}>
                    <i className="bi bi-trash"></i> Excluir
                  </button>
                )}
                {isEditMode && (
                  <button type="button" className="btn btn-secondary me-2 d-print-none" onClick={() => navigate('/dashboard/weekly/list')}>
                    Cancelar
                  </button>
                )}
                <button type="submit" className="btn btn-primary d-print-none">
                  {isEditMode ? 'Salvar Alterações' : 'Salvar Semanário'}
                </button>
              </div>
            </>
          ) : (
            <div className="alert alert-info">
              Por favor, selecione qual semana do ano você gostaria de planejar.
            </div>
          )}
        </form>
      </div>

      <WeeklyPrintView
        week={week}
        dates={dates}
        teacherName={teacherName}
        weeklyData={weeklyData}
        showWeekends={showWeekends}
      />
    </DashboardLayout>
  );
};

export default WeeklyForm;

